import { useForm, Controller } from "react-hook-form";
import SelectComponent from "../ui/Select";
import { useEffect, useRef, useState, useContext, useMemo } from "react";
import api from "utils/axios";
import Button from "components/ui/Button";
import { HiXMark } from "react-icons/hi2";
import { FaFacebook, FaInstagram } from "react-icons/fa6";
import Chatbot from "components/chatbot/Chatbot";
import Select from "react-select";
import moment from "moment-timezone";
import { showToast } from "components/ui/toast";
import { AuthContext } from "context/AuthContext";
import { useGetCompaniesQuery } from "apis/company";
import { useAgencyPartiesQuery } from "apis/agencyParty";
import { useGetUserQuery } from "apis/user";
import SearchSelect from "../ui/SearchSelect";

const AGENT_ROLE_OPTIONS = [
  { value: "mainAgent", label: "Main Agent" },
  { value: "coAgent", label: "Co-Agent" },
];

const toAgentOption = (user) => ({
  value: user._id,
  label: user.name,
  subLabel: user.email,
});

const SuburbsComponent = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuburbs, setSelectedSuburbs] = useState(value || []);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Fetch all suggestions once when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const suburbResponse = await api.get("/admin/suburb");
        const suburbData = suburbResponse.data.data;
        setSuggestions(suburbData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Handle input change and filter suggestions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim()) {
      const filtered = suggestions.filter(
        (item) =>
          item.suburb.toLowerCase().includes(value.toLowerCase()) ||
          item.postcode.includes(value),
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const match = suggestions.find(
        (item) => item.suburb.toLowerCase() === inputValue.toLowerCase(),
      );

      if (
        match &&
        !selectedSuburbs.some((suburb) => suburb.suburb === match.suburb)
      ) {
        handleAddSuburb(match);
      }
    }
  };

  const handleAddSuburb = (suburbObj) => {
    if (!selectedSuburbs.some((suburb) => suburb.suburb === suburbObj.suburb)) {
      const newSuburb = {
        suburb: suburbObj.suburb,
        state: suburbObj.state,
        postcode: suburbObj.postcode,
      };

      const updatedSuburbs = [...selectedSuburbs, newSuburb];
      setSelectedSuburbs(updatedSuburbs);
      onChange(updatedSuburbs);
    }

    setInputValue("");
    setShowSuggestions(false);
  };

  const handleRemoveSuburb = (suburbToRemove) => {
    const updatedSuburbs = selectedSuburbs.filter(
      (suburb) => suburb.suburb !== suburbToRemove.suburb,
    );
    setSelectedSuburbs(updatedSuburbs);
    onChange(updatedSuburbs);
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div>
        <div className="flex gap-2 relative" ref={inputRef}>
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter suburb"
              className="form-input w-full border border-mediumgray"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto z-70">
                {filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleAddSuburb(suggestion)}
                  >
                    {suggestion?.suburb} {suggestion?.state}{" "}
                    {suggestion?.postcode}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              const match = suggestions.find(
                (item) =>
                  item.suburb.toLowerCase() === inputValue.toLowerCase(),
              );
              if (match) handleAddSuburb(match);
            }}
            className="black-button"
            disabled={!inputValue.trim()}
          >
            Add
          </Button>
        </div>
      </div>

      {selectedSuburbs?.length >= 1 && (
        <div className="space-y-4 mt-4">
          <p className="text-sm font-medium text-darkergray">
            Selected Suburbs:
          </p>

          {/* Co-agents are chosen once on the user, not per suburb, and cover
              every suburb listed here. */}
          {selectedSuburbs.map((suburb) => (
            <div
              key={`${suburb.suburb}-${suburb.postcode}`}
              className="border border-mediumgray rounded-lg px-4 py-3 bg-lightgray"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-darkergray">
                  {suburb?.suburb} {suburb?.state} {suburb?.postcode}
                </span>
                <button
                  onClick={() => handleRemoveSuburb(suburb)}
                  className="text-darkergray"
                >
                  <HiXMark className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function UserEdit({
  name,
  picture,
  updateUser,
  resyncUser,
  isLoading,
  setIsEditOpen,
  mobile,
  title,
  selectedId,

  suburbs,
  facebookUrl,
  instagramUrl,
  userData,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedAgencyParty, setSelectedAgencyParty] = useState(null);

  // License verification state
  const [licenseVerified, setLicenseVerified] = useState(
    !!userData?.license?.isValid,
  );
  const [verifiedLicenseNumber, setVerifiedLicenseNumber] = useState(
    userData?.license?.number || "",
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  // Get companies data
  const { data: companies = [], isPending: companiesLoading } =
    useGetCompaniesQuery();

  // Get agency parties (conjunction agents) for the search-select
  const { data: agencyParties = [], isPending: agencyPartiesLoading } =
    useAgencyPartiesQuery();

  // Same unfiltered list the Users page loads, so usually already cached.
  const { data: allUsers } = useGetUserQuery();

  // The backend only links agents in the same company or organization.
  const teamCandidates = useMemo(
    () =>
      allUsers.filter(
        (u) =>
          u._id !== selectedId &&
          !u.isDeleted &&
          ((userData?.companyId && u.companyId === userData.companyId) ||
            (userData?.organization?._id &&
              u.organization?._id === userData.organization._id)),
      ),
    [allUsers, selectedId, userData],
  );
  const mainAgentOptions = teamCandidates
    .filter((u) => !u.mainAgentId)
    .map(toAgentOption);
  // A co-agent already working for someone else is moved there first, not added here.
  const coAgentOptions = teamCandidates
    .filter((u) => !u.mainAgentId || u.mainAgentId === selectedId)
    .map(toAgentOption);
  const savedCoAgentIds = useMemo(
    () =>
      allUsers
        .filter((u) => u.mainAgentId === selectedId && !u.isDeleted)
        .map((u) => u._id),
    [allUsers, selectedId],
  );

  const titleOptions = [
    { value: "Partner", label: "Partner" },
    { value: "Director", label: "Director" },
    { value: "Associate Agent", label: "Associate Agent" },
    {
      value: "Real Estate Representative",
      label: "Real Estate Representative",
    },
    { value: "Investment Manager", label: "Investment Manager" },
    { value: "Concierge", label: "Concierge" },
    {
      value: "Business Development Manager",
      label: "Business Development Manager",
    },
  ];

  const licenseTypeOptions = [
    { value: "license", label: "License" },
    { value: "certificate", label: "Certificate" },
  ];

  const facebookRegex =
    /^(https?:\/\/)?(www\.)?facebook.com\/[a-zA-Z0-9(\.\?)?]/;
  const instagramRegex =
    /^(https?:\/\/)?(www\.)?instagram.com\/[a-zA-Z0-9(\.\?)?]/;

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name,
      mobile,
      companyId: userData?.companyId || "",
      officeId: userData?.officeId || "",
      title: {
        value: title,
        label: title,
      },
      suburbs: suburbs || [],
      facebookUrl,
      instagramUrl,
      // Presence of an AgencyParty id == conjunction agent; empty == not.
      conjunctionAgentId: userData?.conjunctionAgentId || "",
      agentType: userData?.mainAgentId ? "coAgent" : "mainAgent",
      mainAgentId: userData?.mainAgentId || "",
      // Left undefined until the picker is touched, so an untouched team is
      // never re-sent.
      coAgents: undefined,
      isExempt: userData?.isExempt,
      // License now lives under a nested `license` object.
      license: {
        type: {
          value: userData?.license?.type || "license",
          label:
            userData?.license?.type === "certificate"
              ? "Certificate"
              : "License",
        },
        number: userData?.license?.number || "",
        expiry: userData?.license?.expiry
          ? moment(userData.license.expiry).format("YYYY-MM-DD")
          : "",
        issuedTo: userData?.license?.issuedTo || "",
        url: userData?.license?.url || "",
        isValid: userData?.license?.isValid ?? false,
      },
    },
  });

  const watchAgentType = watch("agentType");

  // Company options for react-select
  const companyOptions = companies.map((company) => ({
    value: company._id,
    label: company.name,
    company,
  }));

  // Office options based on selected company
  const officeOptions =
    selectedCompany?.company?.offices?.map((office) => ({
      value: office._id,
      label: `${office.name} - ${office.address || office.suburb || ""}`,
      office,
    })) || [];

  // Agency party (conjunction agent) options for react-select
  const agencyPartyOptions = agencyParties.map((party) => ({
    value: party._id,
    label: `${party.company}${
      party.licenceNumber ? ` — ${party.licenceNumber}` : ""
    }`,
    party,
  }));

  // Initialize selected company and office from userData
  useEffect(() => {
    if (companies.length > 0 && userData?.companyId) {
      const foundCompany = companies.find(
        (company) => company._id === userData.companyId,
      );
      if (foundCompany) {
        const companyOption = {
          value: foundCompany._id,
          label: foundCompany.name,
          company: foundCompany,
        };
        setSelectedCompany(companyOption);
        setValue("companyId", foundCompany._id);
      }
    }
  }, [companies, userData?.companyId, setValue]);

  // Initialize selected office when company is loaded
  useEffect(() => {
    if (selectedCompany?.company?.offices && userData?.officeId) {
      const foundOffice = selectedCompany.company.offices.find(
        (office) => office._id === userData.officeId,
      );
      if (foundOffice) {
        const officeOption = {
          value: foundOffice._id,
          label: `${foundOffice.name} - ${foundOffice.address || foundOffice.suburb || ""}`,
          office: foundOffice,
        };
        setSelectedOffice(officeOption);
        setValue("officeId", foundOffice._id);
      }
    }
  }, [selectedCompany, userData?.officeId, setValue]);

  // Initialize selected agency party from userData.conjunctionAgentId
  useEffect(() => {
    if (agencyParties.length > 0 && userData?.conjunctionAgentId) {
      const found = agencyParties.find(
        (party) => party._id === userData.conjunctionAgentId,
      );
      if (found) {
        setSelectedAgencyParty({
          value: found._id,
          label: `${found.company}${
            found.licenceNumber ? ` — ${found.licenceNumber}` : ""
          }`,
          party: found,
        });
        setValue("conjunctionAgentId", found._id);
      }
    }
  }, [agencyParties, userData?.conjunctionAgentId, setValue]);

  // Handle company change
  const handleCompanyChange = (selectedOption) => {
    setSelectedCompany(selectedOption);
    setValue("companyId", selectedOption?.value || "");

    // Reset office selection when company changes
    setSelectedOffice(null);
    setValue("officeId", "");
  };

  // Handle office change
  const handleOfficeChange = (selectedOption) => {
    setSelectedOffice(selectedOption);
    setValue("officeId", selectedOption?.value || "");
  };

  // Verify license against NSW registry
  const handleVerifyLicense = async () => {
    const licenseNumber = watch("license.number");
    if (!licenseNumber?.trim()) {
      setVerificationError("Please enter a license number first.");
      return;
    }

    setIsVerifying(true);
    setVerificationError("");

    try {
      const response = await api.get("/admin/user/license/verify", {
        params: { licenceNumber: licenseNumber.trim() },
      });

      if (response.data.success && response.data.data) {
        const result = response.data.data;

        // Auto-fill license fields from verification result
        if (result.type) {
          const typeLabel =
            result.type === "certificate" ? "Certificate" : "License";
          setValue("license.type", { value: result.type, label: typeLabel });
        }
        if (result.issuedTo) setValue("license.issuedTo", result.issuedTo);
        if (result.expiry) {
          setValue(
            "license.expiry",
            moment(result.expiry).format("YYYY-MM-DD"),
          );
        }
        setValue("license.isValid", result.isValid);

        setLicenseVerified(true);
        setVerifiedLicenseNumber(licenseNumber.trim());
        showToast("success", "License verified successfully");
      } else {
        setVerificationError("License verification failed. Please check the number and try again.");
        setLicenseVerified(false);
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        "Failed to verify license. Please try again.";
      setVerificationError(errMsg);
      setLicenseVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // Watch license number to invalidate verification when changed
  const watchedLicenseNumber = watch("license.number");
  useEffect(() => {
    if (
      licenseVerified &&
      watchedLicenseNumber?.trim() !== verifiedLicenseNumber
    ) {
      setLicenseVerified(false);
      setVerificationError("");
    }
  }, [watchedLicenseNumber, licenseVerified, verifiedLicenseNumber]);

  const onSubmit = async (data) => {
    // Block submission if license number is present but not verified
    if (data.license?.number?.trim() && !licenseVerified) {
      setVerificationError(
        "Please verify the license before submitting.",
      );
      return;
    }

    setIsSubmitting(true);

    // Build the nested license object, preserving fields we don't edit here
    // (isExpired, remindersSent) from the existing userData.
    const licenseExpiry = data.license?.expiry
      ? new Date(data.license.expiry).toISOString()
      : null;

    const license = {
      ...(userData?.license || {}),
      type: data.license?.type?.value || "license",
      number: data.license?.number || "",
      expiry: licenseExpiry,
      issuedTo: data.license?.issuedTo || "",
      url: data.license?.url || "",
      isValid: !!data.license?.isValid,
    };

    // The backend treats a present mainAgentId / coAgents as a relationship
    // change and sets agentType itself, so only send what actually changed.
    const isCoAgent = data.agentType === "coAgent";
    const savedMainAgentId = userData?.mainAgentId || "";
    const mainAgentId = isCoAgent
      ? data.mainAgentId !== savedMainAgentId
        ? data.mainAgentId
        : undefined
      : savedMainAgentId
        ? null
        : undefined;
    const coAgents =
      !isCoAgent &&
      data.coAgents &&
      (data.coAgents.length !== savedCoAgentIds.length ||
        data.coAgents.some((id) => !savedCoAgentIds.includes(id)))
        ? data.coAgents
        : undefined;

    await updateUser(
      {
        ...userData,
        ...data,
        id: selectedId,
        companyId: data.companyId,
        officeId: data.officeId,
        title: data.title.value,
        // A co-agent holds no suburbs; they come from the main agent.
        suburbs: isCoAgent
          ? undefined
          : data.suburbs?.map((suburb) => {
              return {
                suburb: suburb.suburb,
                state: suburb.state,
                postcode: suburb.postcode,
              };
            }) || [],
        // Presence of an AgencyParty id == conjunction agent; empty == not.
        conjunctionAgentId: data.conjunctionAgentId || null,
        agentType: userData?.agentType,
        mainAgentId,
        coAgents,
        isExempt: data.isExempt,
        license,
      },
      {
        onSuccess: () => {
          reset();
          setIsSubmitting(false);
          setIsEditOpen(false);
        },
        onError: (error) => {
          setIsSubmitting(false);
          showToast(
            "error",
            error.response?.data?.message || "Failed to update user",
          );
        },
      },
    );
    resyncUser();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result); // Update the image preview
        setHasNewImage(true); // Set the flag that a new image is uploaded
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = async () => {
    try {
      setLoading(true);

      // Use updateUser instead of direct API call
      await updateUser(
        {
          ...userData,
          id: selectedId,
          picture: image,
        },
        {
          onSuccess: () => {
            // Update the user context with the new data
            setUser((prevUser) => ({
              ...prevUser,
              picture: `${image}?${new Date().getTime()}`,
            }));

            // Update the image state with cache buster
            setImage(`${image}?${new Date().getTime()}`);
            showToast("success", "Image updated successfully");
            setHasNewImage(false); // Reset the flag after saving the image
          },
        },
      );
      resyncUser();
    } catch (error) {
      console.log(error.message);
      showToast("error", "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const boxes = useMemo(() => {
    return [
      { id: "details", title: "DETAILS" },
      { id: "image", title: "IMAGE" },
      { id: "story", title: "YOUR STORY" },
      { id: "vision", title: "YOUR VISION" },
    ];
  }, []);

  const [selectedBox, setSelectedBox] = useState("details");
  const { user, setUser } = useContext(AuthContext);
  const [image, setImage] = useState(picture);
  const fileInputRef = useRef(null); // Reference to file input
  const [hasNewImage, setHasNewImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [storyError, setStoryError] = useState("");
  const [visionError, setVisionError] = useState("");

  const [formData, setFormData] = useState({
    story: user?.story,
    vision: user?.vision,
  });

  const [initialFormData, setInitialFormData] = useState({
    story: user?.story,
    vision: user?.vision,
  });

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="bg-white">
      <div className="bg-darkergray p-4 text-white text-center rounded-lg">
        <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#ccc] shadow-inner">
          <img
            src={picture}
            alt="User Avatar"
            className="w-full h-full object-cover"
          />
        </div>
        <h2 className="text-2xl font-bold">{name}</h2>
      </div>

      <div className="flex justify-center my-4">
        <div className="max-w-4xl mx-auto flex overflow-x-auto whitespace-nowrap box-scrollbar scroll-smooth my-scroll-container">
          {boxes.map((box, index) => (
            <div
              key={index}
              data-id={box.id} // Attach data-id for easy selection
              className={`cursor-pointer rounded flex-shrink-0 inline-flex flex-col items-center mr-2 py-2 px-4 relative
              ${box.id === selectedBox ? "bg-mediumgray" : "bg-lightgray"}
            `}
              onClick={() => {
                const element = document.getElementById(box.id);
                if (element) {
                  const offset = 120; // Adjust this value to match the height of your tabs
                  const bodyRect = document.body.getBoundingClientRect().top;
                  const elementRect = element.getBoundingClientRect().top;
                  const elementPosition = elementRect - bodyRect;
                  const offsetPosition = elementPosition - offset - 50;

                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                  });

                  setSelectedBox(box.id);
                }
              }}
            >
              <div className="relative text-center">
                <div className="text-xs m-0">
                  <div className="flex items-center">
                    <h6 className="text-capitalize">
                      {box.title.toLowerCase()}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        id="details"
        className={selectedBox === "details" ? "block" : "hidden"}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-6">
          <div>
            <label className="form-label">Name</label>
            <input
              {...register("name", { required: "Name is required" })}
              type="text"
              placeholder="Enter your name"
              className="w-full px-3 py-2 border border-darkgray rounded-md shadow-sm focus:outline-none focus:ring-2 focus:darkgray focus:border-darkgray"
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>

          {/* Company Selection */}
          <div>
            <label className="form-label">Company *</label>
            <Controller
              name="companyId"
              rules={{ required: "Company is required" }}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={selectedCompany}
                  onChange={(selectedOption) => {
                    handleCompanyChange(selectedOption);
                    field.onChange(selectedOption?.value || "");
                  }}
                  options={companyOptions}
                  placeholder="Select company"
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  isLoading={companiesLoading}
                  isSearchable
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.companyId
                        ? "#ef4444"
                        : baseStyles.borderColor,
                      "&:hover": {
                        borderColor: errors.companyId
                          ? "#ef4444"
                          : baseStyles.borderColor,
                      },
                    }),
                  }}
                />
              )}
            />
            {errors.companyId && (
              <p className="text-red-500 text-sm">{errors.companyId.message}</p>
            )}
          </div>

          {/* Office Selection */}
          <div>
            <label className="form-label">Office *</label>
            <Controller
              name="officeId"
              rules={{ required: "Office is required" }}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={selectedOffice}
                  onChange={(selectedOption) => {
                    handleOfficeChange(selectedOption);
                    field.onChange(selectedOption?.value || "");
                  }}
                  options={officeOptions}
                  placeholder="Select office"
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  isSearchable
                  isDisabled={!selectedCompany}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.officeId
                        ? "#ef4444"
                        : baseStyles.borderColor,
                      "&:hover": {
                        borderColor: errors.officeId
                          ? "#ef4444"
                          : baseStyles.borderColor,
                      },
                    }),
                  }}
                />
              )}
            />
            {errors.officeId && (
              <p className="text-red-500 text-sm">{errors.officeId.message}</p>
            )}
            {!selectedCompany && (
              <p className="text-sm text-darkgray">
                Please select a company first
              </p>
            )}
          </div>

          <div>
            <label className="form-label">Mobile</label>
            <input
              {...register("mobile", { required: "Mobile is required" })}
              type="tel"
              placeholder="Enter your mobile number"
              className="w-full px-3 py-2 border border-darkgray rounded-md shadow-sm focus:outline-none focus:ring-2 focus:darkgray focus:border-darkgray"
            />
            {errors.mobile && (
              <p className="text-red-500 text-sm">{errors.mobile.message}</p>
            )}
          </div>
          <div>
            <label className="form-label">{"Title"}</label>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field }) => (
                <SelectComponent
                  field={field}
                  options={titleOptions}
                  label={"Title"}
                />
              )}
            />
            {errors.title && (
              <p className="text-red-500 text-sm">{errors.title.message}</p>
            )}
          </div>

          {/* License Information */}
          <div className="border border-mediumgray rounded-lg p-4 bg-lightgray space-y-4">
            <p className="text-sm font-medium text-darkergray">
              License Information
            </p>

            <div>
              <label className="form-label">License Type</label>
              <Controller
                name="license.type"
                control={control}
                render={({ field }) => (
                  <SelectComponent
                    field={field}
                    options={licenseTypeOptions}
                    label={"License Type"}
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">License Number</label>
              <div className="flex gap-2">
                <input
                  {...register("license.number")}
                  type="text"
                  placeholder="Enter license number"
                  className="form-input border border-mediumgray flex-1"
                />
                <button
                  type="button"
                  onClick={handleVerifyLicense}
                  disabled={isVerifying}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition duration-150 ease-in-out ${
                    licenseVerified
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-darkergray text-white hover:bg-darkgray"
                  }`}
                >
                  {isVerifying
                    ? "Verifying..."
                    : licenseVerified
                      ? "✓ Verified"
                      : "Verify License"}
                </button>
              </div>
              {verificationError && (
                <p className="text-red-500 text-sm mt-1">
                  {verificationError}
                </p>
              )}
              {licenseVerified && (
                <p className="text-green-600 text-sm mt-1">
                  License has been verified successfully.
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Issued To</label>
              <input
                {...register("license.issuedTo")}
                type="text"
                placeholder="Name on the license"
                className="form-input border border-mediumgray"
                readOnly={licenseVerified}
              />
            </div>

            <div>
              <label className="form-label">Expiry Date</label>
              <input
                {...register("license.expiry")}
                type="date"
                className="form-input border border-mediumgray"
                readOnly={licenseVerified}
              />
            </div>

            <div>
              <label className="form-label">Document URL</label>
              <input
                {...register("license.url")}
                type="text"
                placeholder="License document URL"
                className="form-input border border-mediumgray"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="licenseIsValid"
                {...register("license.isValid")}
                className="h-4 w-4 text-darkgray border-darkgray rounded focus:ring-darkgray"
                disabled={licenseVerified}
              />
              <label
                htmlFor="licenseIsValid"
                className="ml-2 block text-sm text-gray-900"
              >
                Valid License
              </label>
            </div>
          </div>

          <div>
            <label className="form-label ">Facebook</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFacebook className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                {...register("facebookUrl", {
                  pattern: {
                    value: facebookRegex,
                    message: "Invalid facebook url",
                  },
                  required: false,
                })}
                className={`form-input border border-mediumgray pl-10`}
                placeholder="Enter your facebook profile url"
              />
            </div>

            {errors.facebookUrl && (
              <p className="text-red-500 text-sm">
                {errors.facebookUrl.message}
              </p>
            )}
          </div>
          <div>
            <label className="form-label ">Instagram</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaInstagram className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                {...register("instagramUrl", {
                  pattern: {
                    value: instagramRegex,
                    message: "Invalid instagram url",
                  },
                  required: false,
                })}
                className={`form-input border border-mediumgray pl-10`}
                placeholder="Enter your instagram profile url"
              />
            </div>
            {errors.instagramUrl && (
              <p className="text-red-500 text-sm">
                {errors.instagramUrl.message}
              </p>
            )}
          </div>
          {watchAgentType === "coAgent" ? (
            <div>
              <label className="form-label">{"Suburbs"}</label>
              <p className="text-sm text-darkgray">
                A co-agent holds no suburbs. Their alerts come from their main
                agent's suburbs.
              </p>
            </div>
          ) : (
            <div>
              <label className="form-label">{"Suburbs"}</label>
              <Controller
                name="suburbs"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SuburbsComponent
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.suburbs && (
                <p className="text-red-500 text-sm">{errors.suburbs.message}</p>
              )}
            </div>
          )}

          <div>
            <label className="form-label">Agent Role</label>
            <Controller
              name="agentType"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  options={AGENT_ROLE_OPTIONS}
                  value={AGENT_ROLE_OPTIONS.find((o) => o.value === field.value)}
                  onChange={(opt) => field.onChange(opt.value)}
                  searchable={false}
                />
              )}
            />
            <p className="text-xs text-darkgray mt-1">
              {watchAgentType === "coAgent"
                ? userData?.mainAgentId
                  ? `Works for ${
                      allUsers.find((u) => u._id === userData.mainAgentId)
                        ?.name || "their main agent"
                    }.`
                  : "No main agent yet. They are asked to choose one."
                : savedCoAgentIds.length
                  ? `Main agent with ${savedCoAgentIds.length} co-agent${
                      savedCoAgentIds.length === 1 ? "" : "s"
                    }: ${savedCoAgentIds
                      .map((id) => allUsers.find((u) => u._id === id)?.name)
                      .filter(Boolean)
                      .join(", ")}.`
                  : "Main agent with no co-agents."}
            </p>
          </div>

          {watchAgentType === "coAgent" ? (
            <div>
              <label className="form-label">Main Agent *</label>
              <Controller
                name="mainAgentId"
                control={control}
                rules={{
                  validate: (value, formValues) =>
                    formValues.agentType !== "coAgent" ||
                    !!value ||
                    "Main agent is required",
                }}
                render={({ field }) => {
                  const mainAgent = allUsers.find((u) => u._id === field.value);
                  return (
                    <SearchSelect
                      options={mainAgentOptions}
                      value={mainAgent ? toAgentOption(mainAgent) : null}
                      onChange={(opt) => field.onChange(opt?.value || "")}
                      placeholder="Search main agent..."
                      isClearable
                    />
                  );
                }}
              />
              {errors.mainAgentId && (
                <p className="text-red-500 text-sm">
                  {errors.mainAgentId.message}
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="form-label">Co-Agents</label>
              <Controller
                name="coAgents"
                control={control}
                render={({ field }) => (
                  <SearchSelect
                    options={coAgentOptions}
                    value={(field.value ?? savedCoAgentIds)
                      .map((id) => allUsers.find((u) => u._id === id))
                      .filter(Boolean)
                      .map(toAgentOption)}
                    onChange={(selected) =>
                      field.onChange(selected.map((o) => o.value))
                    }
                    placeholder="Search co-agents..."
                    multiple
                  />
                )}
              />
            </div>
          )}

          {/* Conjunction Agent — link an existing Agency Party (optional).
              Selecting one marks the user as a conjunction agent; clearing it
              removes that status. Stored as conjunctionAgentId only. */}
          <div>
            <label className="form-label">Conjunction Agent</label>
            <Controller
              name="conjunctionAgentId"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  options={agencyPartyOptions}
                  value={selectedAgencyParty}
                  onChange={(selectedOption) => {
                    setSelectedAgencyParty(selectedOption);
                    field.onChange(selectedOption?.value || "");
                  }}
                  placeholder={
                    agencyPartiesLoading
                      ? "Loading agency parties…"
                      : "Search by company or licence…"
                  }
                  searchable
                  isClearable
                />
              )}
            />
            <p className="text-xs text-darkgray mt-1">
              Leave empty if this user is not a conjunction agent.
            </p>

            {/* Read-only preview of the selected agency party */}
            {selectedAgencyParty?.party && (
              <div className="text-xs text-darkergray space-y-1 mt-2">
                {selectedAgencyParty.party.abn && (
                  <p>ABN: {selectedAgencyParty.party.abn}</p>
                )}
                {selectedAgencyParty.party.licenceNumber && (
                  <p>Licence: {selectedAgencyParty.party.licenceNumber}</p>
                )}
                {selectedAgencyParty.party.address && (
                  <p>Address: {selectedAgencyParty.party.address}</p>
                )}
              </div>
            )}
          </div>

          {/* Is Exempt Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isExempt"
              {...register("isExempt")}
              className="h-4 w-4 text-darkgray border-darkgray rounded focus:ring-darkgray"
            />
            <label
              htmlFor="isExempt"
              className="ml-2 block text-sm text-gray-900"
            >
              Is Exempt
            </label>
          </div>

          <div>
            <button
              type="submit"
              className="w-full bg-darkergray text-white py-2 px-4 rounded-md hover:bg-darkgray focus:outline-none focus:ring-2 focus:darkgray focus:ring-offset-2 transition duration-150 ease-in-out"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </div>

      {/* Image */}
      <div id="image" className={selectedBox === "image" ? "block" : "hidden"}>
        <img
          src={
            image?.startsWith("data:")
              ? image
              : `${image}?${new Date().getTime()}`
          }
          alt="profile"
          className="w-full h-auto max-w-[200px] max-h-[200px] object-contain mx-auto"
        />

        <div>
          <button onClick={handleButtonClick} className="gray-button">
            Upload Image
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        <Button
          className="black-button float-right mt-4"
          onClick={handleSaveImage}
          loading={loading}
          disabled={loading || !hasNewImage}
        >
          Save Image
        </Button>
      </div>

      {/* Story */}
      <div
        id="story"
        className={selectedBox === "story" ? "block text-start" : "hidden"}
      >
        <h4 className="text-center mb-4">Your Story</h4>
        <Chatbot
          data={{
            name: "story",
            prompt1: "BUSINESS_STORY",
            prompt2: "BUSINESS_STORY_GENERATE",
            initial: "",
            result: formData?.story,
          }}
          onResultChange={async (data) => {
            try {
              setLoading(true);
              // Update local state
              setFormData((prev) => ({
                ...prev,
                story: data,
              }));

              // Use updateUser instead of direct API call
              await updateUser(
                {
                  ...userData,
                  id: selectedId,
                  story: data,
                },
                {
                  onSuccess: () => {
                    showToast("success", "Story updated successfully");
                    setInitialFormData((prev) => ({
                      ...prev,
                      story: data,
                    }));
                  },
                },
              );
              resyncUser();
            } catch (error) {
              console.error("Error saving story:", error);
              showToast("error", "Failed to save story");
            } finally {
              setLoading(false);
            }
          }}
        />
        {storyError && (
          <p className="text-red-500 text-sm text-center">{storyError}</p>
        )}
      </div>

      <div
        id="vision"
        className={selectedBox === "vision" ? "block text-start" : "hidden"}
      >
        <h4 className="text-center mb-4">Your Vision</h4>
        <Chatbot
          data={{
            name: "vision",
            prompt1: "VISION",
            prompt2: "VISION_GENERATE",
            initial:
              "Write a list of your goals and will turn it into a vision of belief",
            result: formData?.vision,
          }}
          onResultChange={async (data) => {
            try {
              setLoading(true);
              // Update local state
              setFormData((prev) => ({
                ...prev,
                vision: data,
              }));

              // Use updateUser instead of direct API call
              await updateUser(
                {
                  ...userData,
                  id: selectedId,
                  vision: data,
                },
                {
                  onSuccess: () => {
                    showToast("success", "Vision updated successfully");
                    setInitialFormData((prev) => ({
                      ...prev,
                      vision: data,
                    }));
                  },
                },
              );
              resyncUser();
            } catch (error) {
              console.error("Error saving vision:", error);
              showToast("error", "Failed to save vision");
            } finally {
              setLoading(false);
            }
          }}
        />
        {visionError && (
          <p className="text-red-500 text-sm text-center">{visionError}</p>
        )}
      </div>
    </div>
  );
}
